import { Injectable } from "@angular/core";
import * as dayjs from 'dayjs';
import jwtDecode from 'jwt-decode';

import { LoginService } from "./login.service";
import { SessionService } from "./session.service";

@Injectable({
  providedIn: 'root'
})
export class AuthTokenService {
  isRefreshingToken = false

  constructor(
    private loginService: LoginService,
    private sessionService: SessionService
  ) {}

  private verifyIfNeedRevalidateAuth() {
    const { token } = this.sessionService.currentUserValue

    if (!token) throw new Error('Missing auth token')

    const decodedToken = jwtDecode(token) as any

    const expiresIn = dayjs.unix(decodedToken?.exp)

    if (!expiresIn.isValid()) throw new Error('Token without expires')

    const secondsToExpireCurrentToken = expiresIn.diff(dayjs(), 'seconds')

    if (secondsToExpireCurrentToken > 60) return false

    return true
  }

  async revalidateCredentials() {
    this.isRefreshingToken = true

    try {
      const { refreshToken } = this.sessionService.currentUserValue

      if(!refreshToken) return null

      const res = await this.loginService.getNewCredentialsWithRefreshToken()

      this.sessionService.setSession(res)

      return res
    } catch (e) {
      return null
    } finally {
      this.isRefreshingToken = false
    }
  }

  async getAuthToken(): Promise<string | null> {
    try {
      const needRevalidateAuth = this.verifyIfNeedRevalidateAuth()

      if(!needRevalidateAuth) return this.sessionService.currentUserValue.token

      const newCredentials = await this.revalidateCredentials()

      if (!newCredentials) throw new Error('Error trying revalidate auth')

      return newCredentials.token
    } catch (e) {
      this.loginService.logout(false)

      return null
    }
  }

  handleGetAuthToken(): Promise<string | null> {
    return new Promise(resolve => {
      if (!this.isRefreshingToken)
        return this.getAuthToken().then(resolve)

      const intervalId = setInterval(() => {
        if (this.isRefreshingToken) return

        this.getAuthToken()
          .then(resolve)
          .finally(() => {
            clearInterval(intervalId)
          })
      }, 1000)
    })
  }
}
